import React, { useState, useEffect, useCallback } from 'react';
import './style.less'

/**
 * 抽屉组件 Props 类型定义
 */
interface DrawerProps {
  /** 是否打开抽屉（受控模式） */
  open?: boolean;
  /** 打开/关闭状态变化时的回调 */
  onOpenChange?: (open: boolean) => void;
  /** 触发打开的按钮文本，默认为"打开抽屉" */
  triggerText?: string;
  /** 抽屉标题，默认为"抽屉面板" */
  title?: string | null;
  /** 抽屉宽度，可以是数字(px)或字符串，默认300 */
  width?: number | string;
  /** 是否显示遮罩层，默认true */
  maskClosable?: boolean;
  /** 是否支持ESC键关闭，默认true */
  keyboard?: boolean;
  /** 抽屉内容，可自定义 */
  children?: React.ReactNode;
  /** 自定义类名 */
  className?: string;
  /** 抽屉打开时的回调 */
  onOpen?: () => void;
  /** 抽屉关闭时的回调 */
  onClose?: () => void;
}

/**
 * 简洁优雅的抽屉组件
 * 支持受控/非受控模式，从右侧滑入，点击遮罩或关闭按钮直接关闭
 */
const Drawer: React.FC<DrawerProps> = ({
  open: controlledOpen,
  onOpenChange,
  title,
  width = 300,
  maskClosable = true,
  keyboard = true,
  children,
  className = "",
  onOpen,
  onClose,
}) => {
  // 内部状态（非受控模式）
  const [internalOpen, setInternalOpen] = useState(false);

  // 判断是否为受控组件
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  // 更新内部状态并触发回调
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
      if (nextOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [isControlled, onOpenChange, onOpen, onClose]
  );

  // 关闭抽屉
  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // ESC 键关闭
  useEffect(() => {
    if (!keyboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        closeDrawer();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [keyboard, isOpen, closeDrawer]);

  // 锁定/解锁 body 滚动（打开时禁止背景滚动）
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // 处理遮罩点击
  const handleMaskClick = () => {
    if (maskClosable) {
      closeDrawer();
    }
  };

  // 阻止抽屉内部点击事件冒泡到遮罩
  const handleDrawerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // 动态计算宽度样式
  const drawerWidth = typeof width === "number" ? `${width}px` : width;

  return (
    <div className={`drawer-wrapper ${className}`} style={{ position: "relative" }}>

      {/* 遮罩层
      <div
        className={`drawer-mask ${isOpen ? "drawer-mask-active" : ""}`}
        onClick={handleMaskClick}
      /> */}

      {/* 抽屉主体 */}
      <div
        className={`drawer-container ${isOpen ? "drawer-open" : ""}`}
        style={{ width: drawerWidth,height:"calc(100% - 87px)" }}
        onClick={handleDrawerClick}
      >
        {title && 
          <div className="drawer-header">
            <h3 className="drawer-title">{title}</h3>
            <button className="drawer-close-btn" onClick={closeDrawer}>
              ✕
            </button>
          </div>
        }

        
        <div className="drawer-body">
          <div className='closeBtnFloat'>
            <button className="drawer-close-btn" onClick={closeDrawer}>
              ✕
            </button>
          </div>
          {children !== undefined ? children : (
            <>
              <p>✨ 这是一个侧边抽屉，从右侧滑入。</p>
              <p>点击「✕」关闭按钮，即可直接关闭。</p>
            </>
          )}
        </div>
        {/* <div className="drawer-footer">
          点击外部或按 ESC 关闭
        </div> */}
      </div>
    </div>
  );
};

export default Drawer;
